/**
 * 1:1 Meeting Assistant Types
 * Help CTOs run effective 1:1s with structured templates and action tracking
 */

// Meeting types
export type MeetingType =
  | 'regular_1on1'
  | 'career_conversation'
  | 'performance_review'
  | 'skip_level'
  | 'onboarding'
  | 'offboarding';

// Action item status
export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

// Action item assignee
export type ActionAssignee = 'manager' | 'report';

// AI suggestion status
export type AIStatus = 'idle' | 'processing' | 'completed' | 'error';

// Team member metadata
export interface TeamMemberMetadata {
  role?: string;
  department?: string;
  start_date?: string;
  career_goals?: string[];
  strengths?: string[];
  growth_areas?: string[];
  preferred_feedback_style?: string;
  notes?: string;
}

// Team member
export interface TeamMember {
  id: string;
  user_id: string;
  name: string;
  email?: string;
  avatar_url?: string;
  metadata: TeamMemberMetadata;
  created_at: string;
  updated_at: string;
}

// Agenda item
export interface AgendaItem {
  id: string;
  content: string;
  added_by: ActionAssignee;
  discussed: boolean;
  notes?: string;
  order: number;
}

// Talking point (pre-defined or AI-suggested)
export interface TalkingPoint {
  id: string;
  content: string;
  category: 'work' | 'growth' | 'feedback' | 'personal' | 'team';
  source: 'template' | 'ai' | 'custom';
  discussed: boolean;
}

// Note section
export interface NoteSection {
  id: string;
  title: string;
  content: string;
  updated_at: string;
}

// Meeting notes structure
export interface MeetingNotes {
  shared: NoteSection[];  // Visible to both parties
  private: NoteSection[]; // Manager only
}

// Action item
export interface ActionItem {
  id: string;
  meeting_id: string;
  content: string;
  assigned_to: ActionAssignee;
  status: ActionStatus;
  due_date?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

// AI-suggested topic
export interface SuggestedTopic {
  id: string;
  content: string;
  reason: string;
  category: TalkingPoint['category'];
  priority: 'high' | 'medium' | 'low';
}

// Meeting
export interface Meeting {
  id: string;
  team_member_id: string;
  meeting_type: MeetingType;
  scheduled_at?: string;
  completed_at?: string;
  duration_minutes?: number;
  agenda_items: AgendaItem[];
  talking_points: TalkingPoint[];
  notes: MeetingNotes;
  mood_rating?: number;  // 1-5 scale
  summary?: string;
  ai_status: AIStatus;
  suggested_topics?: SuggestedTopic[];
  created_at: string;
  updated_at: string;
}

// Meeting with team member info (for display)
export interface MeetingWithMember extends Meeting {
  team_member: TeamMember;
}

// Create meeting request
export interface CreateMeetingRequest {
  team_member_id: string;
  meeting_type: MeetingType;
  scheduled_at?: string;
}

// Update meeting request
export interface UpdateMeetingRequest {
  meeting_type?: MeetingType;
  scheduled_at?: string;
  completed_at?: string;
  duration_minutes?: number;
  agenda_items?: AgendaItem[];
  talking_points?: TalkingPoint[];
  notes?: MeetingNotes;
  mood_rating?: number;
  summary?: string;
}

// Create team member request
export interface CreateTeamMemberRequest {
  name: string;
  email?: string;
  avatar_url?: string;
  metadata?: Partial<TeamMemberMetadata>;
}

// Update team member request
export interface UpdateTeamMemberRequest {
  name?: string;
  email?: string;
  avatar_url?: string;
  metadata?: Partial<TeamMemberMetadata>;
}

// Create action item request
export interface CreateActionItemRequest {
  meeting_id: string;
  content: string;
  assigned_to: ActionAssignee;
  due_date?: string;
}

// Update action item request
export interface UpdateActionItemRequest {
  content?: string;
  assigned_to?: ActionAssignee;
  status?: ActionStatus;
  due_date?: string;
}

// Feature access for premium gating
export interface OneOnOneFeatures {
  canEdit: boolean;
  canSave: boolean;
  maxTeamMembers: number;
  maxMeetingsPerMonth: number;
  historyMonths: number;
  canUseAI: boolean;
  canExport: boolean;
}

// Meeting type configuration
export const MEETING_TYPE_CONFIG: Record<MeetingType, {
  label: string;
  description: string;
  icon: string;
  defaultDuration: number;
  suggestedFrequency: string;
}> = {
  regular_1on1: {
    label: 'Regular 1:1',
    description: 'Recurring check-in meeting',
    icon: '💬',
    defaultDuration: 30,
    suggestedFrequency: 'Weekly or bi-weekly',
  },
  career_conversation: {
    label: 'Career Conversation',
    description: 'Focused on growth and career development',
    icon: '🚀',
    defaultDuration: 60,
    suggestedFrequency: 'Quarterly',
  },
  performance_review: {
    label: 'Performance Review',
    description: 'Formal performance evaluation',
    icon: '📊',
    defaultDuration: 60,
    suggestedFrequency: 'Semi-annually or annually',
  },
  skip_level: {
    label: 'Skip Level',
    description: 'Meeting with indirect report',
    icon: '⬆️',
    defaultDuration: 30,
    suggestedFrequency: 'Quarterly',
  },
  onboarding: {
    label: 'Onboarding',
    description: 'New team member onboarding',
    icon: '👋',
    defaultDuration: 45,
    suggestedFrequency: 'First 90 days',
  },
  offboarding: {
    label: 'Offboarding',
    description: 'Departing team member conversation',
    icon: '👋',
    defaultDuration: 45,
    suggestedFrequency: 'Once',
  },
};

// Action status configuration
export const ACTION_STATUS_CONFIG: Record<ActionStatus, {
  label: string;
  color: string;
  icon: string;
}> = {
  pending: {
    label: 'Pending',
    color: '#94A3B8',
    icon: '⏳',
  },
  in_progress: {
    label: 'In Progress',
    color: '#3B82F6',
    icon: '🔄',
  },
  completed: {
    label: 'Completed',
    color: '#10B981',
    icon: '✅',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#6B7280',
    icon: '❌',
  },
};

// Talking point categories
export const TALKING_POINT_CATEGORIES: Record<TalkingPoint['category'], {
  label: string;
  color: string;
  icon: string;
}> = {
  work: {
    label: 'Work & Projects',
    color: '#3B82F6',
    icon: '💼',
  },
  growth: {
    label: 'Growth & Development',
    color: '#8B5CF6',
    icon: '🌱',
  },
  feedback: {
    label: 'Feedback',
    color: '#F59E0B',
    icon: '💭',
  },
  personal: {
    label: 'Personal & Wellbeing',
    color: '#EC4899',
    icon: '❤️',
  },
  team: {
    label: 'Team & Collaboration',
    color: '#10B981',
    icon: '👥',
  },
};

// Default talking points by meeting type
export const DEFAULT_TALKING_POINTS: Record<MeetingType, TalkingPoint[]> = {
  regular_1on1: [
    { id: '1', content: 'How are you feeling this week?', category: 'personal', source: 'template', discussed: false },
    { id: '2', content: 'What\'s going well? Any wins to celebrate?', category: 'work', source: 'template', discussed: false },
    { id: '3', content: 'What challenges are you facing?', category: 'work', source: 'template', discussed: false },
    { id: '4', content: 'Is there anything blocking your progress?', category: 'work', source: 'template', discussed: false },
    { id: '5', content: 'How can I better support you?', category: 'feedback', source: 'template', discussed: false },
  ],
  career_conversation: [
    { id: '1', content: 'Where do you see yourself in 1-2 years?', category: 'growth', source: 'template', discussed: false },
    { id: '2', content: 'What skills do you want to develop?', category: 'growth', source: 'template', discussed: false },
    { id: '3', content: 'What type of work energizes you most?', category: 'growth', source: 'template', discussed: false },
    { id: '4', content: 'Are there projects or roles you\'d like to explore?', category: 'growth', source: 'template', discussed: false },
    { id: '5', content: 'What support do you need to achieve your goals?', category: 'growth', source: 'template', discussed: false },
  ],
  performance_review: [
    { id: '1', content: 'Review key accomplishments this period', category: 'work', source: 'template', discussed: false },
    { id: '2', content: 'Discuss areas for improvement', category: 'feedback', source: 'template', discussed: false },
    { id: '3', content: 'Review progress against goals', category: 'work', source: 'template', discussed: false },
    { id: '4', content: 'Set goals for next period', category: 'growth', source: 'template', discussed: false },
    { id: '5', content: 'Discuss compensation and recognition', category: 'work', source: 'template', discussed: false },
  ],
  skip_level: [
    { id: '1', content: 'How are things going with your team?', category: 'team', source: 'template', discussed: false },
    { id: '2', content: 'What\'s your manager doing well?', category: 'feedback', source: 'template', discussed: false },
    { id: '3', content: 'What could be improved in your team?', category: 'team', source: 'template', discussed: false },
    { id: '4', content: 'Are there any concerns you want to share?', category: 'feedback', source: 'template', discussed: false },
    { id: '5', content: 'What would make your work more impactful?', category: 'work', source: 'template', discussed: false },
  ],
  onboarding: [
    { id: '1', content: 'How are you settling in?', category: 'personal', source: 'template', discussed: false },
    { id: '2', content: 'Do you have everything you need to do your job?', category: 'work', source: 'template', discussed: false },
    { id: '3', content: 'What questions do you have about the team/company?', category: 'team', source: 'template', discussed: false },
    { id: '4', content: 'Review 30/60/90 day plan', category: 'work', source: 'template', discussed: false },
    { id: '5', content: 'What early wins can we help you achieve?', category: 'growth', source: 'template', discussed: false },
  ],
  offboarding: [
    { id: '1', content: 'What prompted your decision to leave?', category: 'feedback', source: 'template', discussed: false },
    { id: '2', content: 'What did you enjoy most about working here?', category: 'feedback', source: 'template', discussed: false },
    { id: '3', content: 'What could we have done better?', category: 'feedback', source: 'template', discussed: false },
    { id: '4', content: 'Any suggestions for your successor?', category: 'work', source: 'template', discussed: false },
    { id: '5', content: 'Knowledge transfer and handoff plan', category: 'work', source: 'template', discussed: false },
  ],
};
